// The ONE place the web app talks to the server. Every path, body and response shape from
// DESIGN.md §7 (as amended by §11.6) lives here, so a mismatch with the server is fixed in one file.
//
// Rules: every request sends cookies (credentials: 'include'); every mutating request sends
// the header X-Requested-With: fetch. Errors come back as { error: { code, message } }.

import type { SegmentInfo, SelfTestStep, HealthEventType, BlockReason, MessageStatus, SendKind, ProviderId } from '@bt/core';

// ---------- types (what the server sends) ----------

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'agent';
  /** the server sends `disabled`; withDisabledAt() fills disabled_at from it so the pages have one field to read */
  disabled?: boolean;
  disabled_at: string | null;
  last_seen_at?: string | null;
  created_at?: string;
}

export interface UserSummary {
  id: string;
  name: string;
  role?: 'admin' | 'agent';
  disabled?: boolean;
  disabled_at?: string | null;
}

export interface Workspace {
  id: string;
  name: string;
  company_name: string | null;
  timezone: string;
  send_window_start: string; // "08:00" (may arrive as "08:00:00")
  send_window_end: string;
  quiet_reply_exception: boolean;
  allow_quiet_hours_override: boolean;
  default_assignee_user_id: string | null;
  alert_webhook_url: string | null;
  /** phase 2 (DESIGN §12): the AI helper switch, the free-text instructions, and the per-day cap for first texts on a phone line */
  ai_enabled?: boolean;
  ai_instructions?: string | null;
  phone_daily_first_touch_cap?: number;
}

export interface LineHealth {
  last_heartbeat_at: string | null;
  heartbeat_age_s: number | null;
  heartbeat_median_gap_s: number | null;
  heartbeat_alarm_after_s: number | null;
  fcm_token_present: boolean | null;
  device_rate_per_minute: number | null;
  queue_depth_local: number;
  queue_depth_deferred: number;
  /** optional split of queue_depth_local; when absent the page shows the combined number */
  queue_eligible?: number | null;
  queue_accepted?: number | null;
  oldest_queued_at: string | null;
  webhook_url_registered: string | null;
  webhook_events_registered: string[] | null;
  webhook_config_ok: boolean | null;
  last_error_code: string | null;
  last_error_at: string | null;
  last_inbound_webhook_at: string | null;
  last_outbound_sent_at: string | null;
  status: 'ok' | 'warn' | 'alarm' | 'unknown';
  detail: string | null;
  checked_at: string | null;
}

export interface Line {
  id: string;
  phone_number: string;
  label: string | null;
  provider: ProviderId;
  /** secrets are expected to be redacted by the server; the UI only checks presence of keys */
  provider_config?: Record<string, unknown>;
  webhook_url?: string;
  device_rate_per_minute: number | null;
  heartbeat_alarm_minutes?: number | null;
  paused_until: string | null;
  /** optional server hint; 'infinity' does not survive JSON, so the server may send this instead */
  paused?: boolean;
  paused_reason: string | null;
  risk_acknowledged_at?: string | null;
  last_self_test_passed_at?: string | null;
  active: boolean;
  health?: LineHealth | null;
  /** optional server hint; otherwise derived from provider_config */
  signature_configured?: boolean;
}

/** what /auth/me lists; the full Line comes from /lines */
export type LineSummary = Pick<Line, 'id' | 'phone_number' | 'label' | 'provider' | 'active'> & Partial<Line>;

export interface MeResponse {
  user: User;
  workspace: Workspace;
  lines: LineSummary[];
  /** GUESS: the assign dropdown needs everyone; expected on /me. Falls back to GET /users. */
  users?: UserSummary[];
}

export interface OptOut {
  workspace_id?: string;
  phone_number: string;
  reason: 'stop_keyword' | 'manual' | 'provider';
  created_by?: string | null;
  created_by_user?: UserSummary | null;
  created_at: string;
  detail?: string | null;
}

export interface Contact {
  id: string;
  phone_number: string;
  name: string | null;
  timezone: string | null;
  /** where the service call goes; filled from a list or typed on the contact */
  address?: string | null;
  notes: string | null;
  opted_out?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ConversationSummary {
  id: string;
  status: 'open' | 'closed';
  assigned_user: UserSummary | null;
  assigned_user_id?: string | null;
  unread_count: number;
  unread_for_me?: boolean;
  opt_out_suspected?: boolean;
  last_message_at: string;
  last_inbound_at?: string | null;
  last_message: { body: string; direction: 'in' | 'out'; status: MessageStatus; created_at: string; kind?: SendKind; attachments?: { url: string; contentType?: string }[] } | null;
  contact: { id: string; name: string | null; phone_number: string; opted_out: boolean };
  line_id: string;
}

export interface Conversation {
  id: string;
  status: 'open' | 'closed';
  assigned_user_id: string | null;
  assigned_user?: UserSummary | null;
  unread_count: number;
  last_message_at: string;
  last_inbound_at: string | null;
  opt_out_suspected: boolean;
  line_id: string;
  contact_id: string;
  created_at?: string;
}

export interface Message {
  id: string;
  seq: number;
  direction: 'in' | 'out';
  kind: SendKind;
  body: string;
  attachments: { url: string; contentType?: string }[];
  status: MessageStatus;
  provider_status: string | null;
  provider_error_code: string | null;
  provider_error_detail?: string | null;
  segments: number | null;
  encoding: 'gsm7' | 'ucs2' | null;
  blocked_reason: BlockReason | string | null;
  not_before: string | null;
  override_reason?: string | null;
  /** set when the text came from an AI suggestion (the bubble shows "AI-assisted") */
  ai_suggestion_id?: string | null;
  sent_by_user: UserSummary | null;
  created_at: string;
  sent_at: string | null;
  delivered_at: string | null;
  failed_at: string | null;
  received_at: string | null;
  conversation_id?: string;
  line_id?: string;
  to_number?: string;
  from_number?: string;
}

export type EventType =
  | 'note' | 'assigned' | 'unassigned' | 'closed' | 'reopened' | 'opted_out' | 'opted_in'
  | 'opt_out_suspected' | 'opt_out_dismissed' | 'self_test' | 'line_changed' | 'quiet_hours_override' | 'system';

export interface ConversationEvent {
  id: string;
  seq: number;
  type: EventType;
  user_id: string | null;
  user?: UserSummary | null;
  body: string | null;
  data: Record<string, unknown>;
  created_at: string;
}

export type TimelineItem = { type: 'message'; message: Message } | { type: 'event'; event: ConversationEvent };

export interface ConversationResponse {
  conversation: Conversation;
  contact: Contact;
  opted_out: boolean;
  opt_out?: OptOut | null;
  timeline: TimelineItem[];
  next_cursor: string | number | null;
}

export interface PolicyResponse {
  decision: 'send' | 'defer' | 'block';
  reason?: BlockReason;
  not_before?: string | null;
  notBefore?: string | null; // tolerate the core casing
  warnings: string[];
  segments?: SegmentInfo;
  timezone_used?: string;
  timezoneUsed?: string;
  timezone_source?: string;
}

export interface SendResponse {
  message: Message;
  policy: PolicyResponse | null;
  /** 200 = same key seen before (existed) */
  httpStatus: number;
}

export interface Counts {
  open: number;
  mine: number;
  unassigned: number;
  closed: number;
  needs_attention: number;
  /** optional; when absent the shell counts unread_for_me in the Mine list */
  mine_unread?: number;
}

export interface ScheduledMessage {
  /** when it will really go out: the asked time, or the next window open in every zone of the contact */
  effective_send_at?: string | null;
  send_note?: string;
  /** before it goes out: the longest wording with these values; after: what was really sent */
  segments?: number | null;
  encoding?: 'gsm7' | 'ucs2' | null;
  worst_segments?: number | null;
  wordings?: { index: number; rendered: string; length: number; capacity: number; segments: number; encoding: 'gsm7' | 'ucs2' }[];
  id: string;
  contact: { id: string; name: string | null; phone_number: string } | null;
  contact_id?: string;
  line_id: string;
  conversation_id: string | null;
  send_at: string;
  template_id: string | null;
  template?: { id: string; name: string } | null;
  body: string | null;
  variables: Record<string, string>;
  status: 'pending' | 'held' | 'queued' | 'sent' | 'cancelled' | 'skipped_opted_out' | 'failed';
  held_reason: string | null;
  attempt?: number;
  message_id: string | null;
  error: string | null;
  preview?: string | null;
  created_at: string;
}

export type TemplateCategory = 'first_message' | 'reminder' | 'other';

export interface Template {
  /** per wording: length/segments once the long sample values are filled in (server-side estimate) */
  wordings?: { index: number; worst_length: number; worst_capacity: number; worst_segments: number; encoding: 'gsm7' | 'ucs2' }[];
  worst_segments?: number | null;
  id: string;
  name: string;
  variants: string[];
  /** phase 2: first_message | reminder | other (the server defaults to 'other') */
  category?: TemplateCategory;
  /** the server's "fewer than 3 wordings" text, when it sends one */
  warning?: string | null;
  in_use_pending?: number;
  created_at?: string;
  updated_at?: string;
  /** optional: how many pending reminders use it (the "cannot drop to one variant" rule) */
  pending_uses?: number;
}

export interface HealthEvent {
  id: string;
  line_id: string | null;
  type: HealthEventType;
  severity: 'warn' | 'alarm';
  details: Record<string, unknown>;
  opened_at: string;
  acknowledged_at: string | null;
  /** who pressed Acknowledge (the server joins the user's name); null until someone does */
  acknowledged_by?: { id: string; name: string | null } | null;
  resolved_at: string | null;
  /** optional plain-words text attached by the API; otherwise core.ALARM_ACTIONS[type] */
  what?: string;
  do_this?: string[];
  action?: { what: string; doThis: string[] };
}

export interface WebhookEvent {
  id: string;
  provider: string;
  line_id: string | null;
  event_type: string | null;
  provider_event_id: string | null;
  outcome: 'processing' | 'inbound_created' | 'status_updated' | 'ignored' | 'duplicate' | 'error';
  detail: string | null;
  received_at: string;
}

export interface HealthResponse {
  lines: Line[];
  alarms: HealthEvent[];
  recent_errors: Message[];
  counters: { queued: number; sending: number; accepted?: number; sent_24h: number; delivered_24h: number; failed_24h: number; inbound_24h: number; staff_alerts_today?: number };
  /** GUESS (phase 2): the AI month line, when the health route includes it; otherwise the page asks GET /api/ai/usage */
  ai?: { month_cost_usd: number | string; suggestions_month: number } | null;
  repetition: { fingerprint: string; sample_body: string; count_24h: number }[];
  /** GUESS: the "recent webhook events tail"; hidden when absent */
  recent_webhook_events?: WebhookEvent[];
  /** what the server actually sends; health.get() copies it into recent_webhook_events */
  webhook_events?: WebhookEvent[];
}

export interface SelfTest {
  id: string;
  line_id: string;
  to_number: string;
  code: string;
  status: 'running' | 'passed' | 'failed';
  steps: SelfTestStep[];
  started_at: string;
  timeout_at: string;
  finished_at: string | null;
  diagnosis: string | null;
}

export interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

// ---------- phase 2 types (DESIGN §12) ----------

/** A file on disk the server serves at /public/assets/:token (what the phone fetches for MMS). */
export interface Asset {
  id: string;
  kind: 'image' | 'pdf';
  filename: string;
  content_type: string;
  size_bytes: number;
  width?: number | null;
  height?: number | null;
  public_token?: string;
  /** ASSUMED: the server sends the public URL; when it is relative the web makes it absolute before sending it to a phone */
  public_url?: string;
  /** e.g. "may be too big for MMS" (images over 1 MB) */
  warning?: string | null;
  created_at?: string;
}

export type KnowledgeKind = 'faq' | 'price' | 'picture' | 'note';

export interface KnowledgeItem {
  id: string;
  kind: KnowledgeKind;
  title: string;
  body: string | null;
  price_cents: number | null;
  asset_id: string | null;
  /** ASSUMED: pictures come with their asset so the grid can show a thumbnail */
  asset?: Asset | null;
  tags?: string[];
  active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface AiUsage {
  input_tokens: number;
  output_tokens: number;
  cost_usd: number | string;
}

export interface AiSuggestion {
  text: string;
  attach_asset_id: string | null;
  why: string;
  /** optional: the server may resolve the asset to a URL for the thumbnail */
  attach_url?: string | null;
}

export interface AiSuggestResponse {
  /** the ai_suggestions row id; POST /api/ai/suggestions/:id/choose takes it */
  id: string;
  suggestion_id?: string;
  suggestions: AiSuggestion[];
  provider?: string;
  model?: string;
  usage?: AiUsage;
}

export interface AiUsageResponse {
  configured: boolean;
  enabled?: boolean;
  provider: string | null;
  model: string | null;
  month: { cost_usd: number | string; suggestions: number; input_tokens?: number; output_tokens?: number };
}

export type ImportStatus = 'extracting' | 'review' | 'scheduled' | 'cancelled' | 'failed';

/** 'upload' = read from a picture or PDF; 'manual' = typed in by the office */
export type ImportSource = 'upload' | 'manual';

/** one row of a read list; `issues` are the server's plain-word reasons (duplicate, opted out, bad number, ...) */
export interface ImportRow {
  name: string | null;
  phone_raw: string | null;
  phone_e164: string | null;
  address: string | null;
  notes: string | null;
  appointment_date: string | null; // "2026-09-04"
  appointment_time: string | null; // "14:00", or a window as typed: "2-4pm"
  issues: string[];
}

export interface ListImport {
  id: string;
  status: ImportStatus;
  source?: ImportSource;
  asset_ids: string[];
  assets?: Asset[];
  note: string | null;
  rows: ImportRow[];
  provider?: string | null;
  model?: string | null;
  cost_usd: number | string | null;
  template_id: string | null;
  line_id: string | null;
  send_at: string | null;
  scheduled_count: number | null;
  skipped: { row: number; reason: string }[];
  error: string | null;
  created_by?: UserSummary | string | null;
  created_at: string;
  updated_at?: string;
}

export interface ImportScheduleBody {
  template_id: string;
  line_id?: string;
  /** one time for everyone (ISO) ... */
  send_at?: string;
  /** ... or each row's own appointment plus an offset */
  per_row_appointment?: boolean;
  offset?: { preset: 'day_before_6pm' | 'two_hours_before' | 'custom'; minutes_before?: number };
}

export interface ImportScheduleResponse {
  scheduled_count: number;
  skipped: { row: number; reason: string }[];
  first_send_at?: string | null;
  last_send_at?: string | null;
  /** how many rolled to later days because of the per-day first-touch cap */
  rolled_over?: number;
  note?: string | null;
  import?: ListImport;
}

export interface NotificationPrefs {
  notify_sms_number: string | null;
  notify_sms: boolean;
  notify_push: boolean;
  /** null when the server has no VAPID keys (browser push unavailable) */
  vapid_public_key: string | null;
  push_subscriptions?: number;
}

// ---------- the fetch wrapper ----------

export class ApiError extends Error {
  status: number;
  code: string;
  details: Record<string, unknown>;
  constructor(status: number, code: string, message: string, details: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** fired on a 401 so the app can send the person to the login screen */
export const UNAUTHORIZED_EVENT = 'bt:unauthorized';

async function request<T>(method: string, path: string, body?: unknown): Promise<{ data: T; status: number }> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (method !== 'GET') headers['X-Requested-With'] = 'fetch';
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network', 'Cannot reach the server. Check your connection.');
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  if (!res.ok) {
    if (res.status === 401 && !path.startsWith('/api/auth/login')) {
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    }
    const err = (data as { error?: { code?: string; message?: string } } | null)?.error;
    const details = (err && typeof err === 'object' ? (err as Record<string, unknown>) : {}) as Record<string, unknown>;
    throw new ApiError(res.status, err?.code ?? `http_${res.status}`, err?.message ?? `Request failed (${res.status})`, details);
  }
  return { data: (data ?? {}) as T, status: res.status };
}

const get = <T,>(path: string) => request<T>('GET', path).then((r) => r.data);
const post = <T,>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}).then((r) => r.data);
const patch = <T,>(path: string, body: unknown) => request<T>('PATCH', path, body).then((r) => r.data);
const del = <T,>(path: string) => request<T>('DELETE', path).then((r) => r.data);

/** Multipart upload (pictures, list files). Same cookie + CSRF header rules; the browser sets the boundary. */
async function upload<T>(path: string, form: FormData): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'fetch' }, credentials: 'include', body: form });
  } catch {
    throw new ApiError(0, 'network', 'Cannot reach the server. Check your connection.');
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    const err = (data as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(res.status, err?.code ?? `http_${res.status}`, err?.message ?? `Upload failed (${res.status})`, (err ?? {}) as Record<string, unknown>);
  }
  return (data ?? {}) as T;
}

/** A phone cannot fetch "/public/assets/x"; make it absolute for MMS. Already-absolute URLs pass through. */
export function absoluteUrl(u: string): string {
  try {
    return new URL(u, window.location.origin).toString();
  } catch {
    return u;
  }
}

/** Where an asset can be seen. Prefers the server's public_url; falls back to the token route from DESIGN §12.3. */
export function assetUrl(a: Pick<Asset, 'public_url' | 'public_token'> | null | undefined): string | null {
  if (!a) return null;
  if (a.public_url) return a.public_url;
  if (a.public_token) return `/public/assets/${a.public_token}`;
  return null;
}

/** numeric columns arrive as strings; "$0.0123" wants a number */
export function money(x: number | string | null | undefined): number {
  const n = typeof x === 'string' ? Number(x) : (x ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function qs(params: Record<string, string | number | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** The server says { disabled: true }; the pages read disabled_at. Give them a value either way. */
function withDisabledAt<T extends { disabled?: boolean; disabled_at?: string | null }>(u: T): T {
  return { ...u, disabled_at: u.disabled_at ?? (u.disabled ? 'disabled' : null) };
}

// ---------- endpoints ----------

export const api = {
  auth: {
    login: (email: string, password: string) => post<{ user: User }>('/api/auth/login', { email, password }),
    logout: () => post<Record<string, never>>('/api/auth/logout'),
    me: () =>
      get<MeResponse>('/api/auth/me').then((r) => ({ ...r, user: withDisabledAt(r.user), users: r.users?.map(withDisabledAt) })),
  },

  conversations: {
    list: (p: { box: 'open' | 'closed' | 'mine' | 'unassigned' | 'all'; q?: string; cursor?: string | null; limit?: number }) =>
      get<Page<ConversationSummary>>(`/api/conversations${qs({ box: p.box, q: p.q, cursor: p.cursor, limit: p.limit ?? 50 })}`),
    counts: () => get<Counts>('/api/conversations/counts'),
    create: (p: { phone_number?: string; contact_id?: string; line_id?: string }) =>
      post<{ conversation: Conversation | ConversationSummary }>('/api/conversations', p),
    get: (id: string, before?: string | number | null) =>
      get<ConversationResponse>(`/api/conversations/${id}${qs({ before: before ?? undefined })}`),
    send: async (
      id: string,
      p: { body: string; idempotency_key: string; override?: { reason: string } | null; attachments?: { url: string; contentType?: string }[] },
    ): Promise<SendResponse> => {
      const r = await request<{ message: Message; policy: PolicyResponse | null }>('POST', `/api/conversations/${id}/messages`, p);
      return { message: r.data.message, policy: r.data.policy ?? null, httpStatus: r.status };
    },
    resend: (id: string, messageId: string) =>
      post<{ message: Message; policy: PolicyResponse | null }>(`/api/conversations/${id}/messages/${messageId}/resend`),
    /** cancel a text still waiting in the outbox (queued); 409 once it has left */
    cancelMessage: (id: string, messageId: string) => post<{ message: Message }>(`/api/conversations/${id}/messages/${messageId}/cancel`),
    markRead: (id: string, up_to_seq: number) => post<Record<string, never>>(`/api/conversations/${id}/read`, { up_to_seq }),
    assign: (id: string, user_id: string | null, note?: string) =>
      post<{ conversation: Conversation }>(`/api/conversations/${id}/assign`, note ? { user_id, note } : { user_id }),
    setStatus: (id: string, status: 'open' | 'closed') => post<{ conversation: Conversation }>(`/api/conversations/${id}/status`, { status }),
    addNote: (id: string, body: string) => post<{ event: ConversationEvent }>(`/api/conversations/${id}/notes`, { body }),
    /** the yellow "may have opted out" banner. confirm = block the number, dismiss = not an opt-out (the server calls the first one 'block') */
    optOutSuspected: (id: string, action: 'confirm' | 'dismiss') =>
      post<{ conversation?: Conversation; opted_out?: boolean }>(`/api/conversations/${id}/opt-out-suspected`, { action: action === 'confirm' ? 'block' : 'dismiss' }),
  },

  contacts: {
    list: (p: { q?: string; cursor?: string | null; limit?: number }) =>
      get<Page<Contact>>(`/api/contacts${qs({ q: p.q, cursor: p.cursor, limit: p.limit ?? 50 })}`),
    create: (p: { name?: string; phone_number: string; timezone?: string | null; address?: string | null }) => post<{ contact: Contact }>('/api/contacts', p),
    get: (id: string) => get<{ contact: Contact; opted_out: boolean; opt_out: OptOut | null; conversations: ConversationSummary[] }>(`/api/contacts/${id}`),
    update: (id: string, p: { name?: string | null; timezone?: string | null; address?: string | null; notes?: string | null }) => patch<{ contact: Contact }>(`/api/contacts/${id}`, p),
    optOut: (id: string, action: 'block' | 'unblock', reason?: string) =>
      post<{ opted_out: boolean }>(`/api/contacts/${id}/opt-out`, reason ? { action, reason } : { action }),
  },

  optOuts: {
    list: (cursor?: string | null) => get<Page<OptOut>>(`/api/opt-outs${qs({ cursor })}`),
  },

  scheduled: {
    list: (status: 'pending' | 'held' | 'needs_attention' | 'all') => get<{ items: ScheduledMessage[] }>(`/api/scheduled${qs({ status })}`),
    create: (p: { contact_id: string; line_id?: string; send_at: string; template_id?: string; body?: string; variables?: Record<string, string> }) =>
      post<{ scheduled: ScheduledMessage; preview: string; policy_note?: string | null; segment_note?: string | null; segments?: { worst: number; wordings: ScheduledMessage['wordings'] } }>('/api/scheduled', p),
    cancel: (id: string) => del<Record<string, never>>(`/api/scheduled/${id}`),
  },

  templates: {
    list: () => get<{ items: Template[] }>('/api/templates'),
    create: (p: { name: string; variants: string[]; category?: TemplateCategory }) => post<{ template: Template }>('/api/templates', p),
    update: (id: string, p: { name?: string; variants?: string[]; category?: TemplateCategory }) => patch<{ template: Template }>(`/api/templates/${id}`, p),
    remove: (id: string) => del<Record<string, never>>(`/api/templates/${id}`),
  },

  lines: {
    list: () => get<{ items: Line[] }>('/api/lines'),
    create: (p: {
      phone_number: string;
      label?: string;
      provider: ProviderId;
      provider_config: Record<string, unknown>;
      device_rate_per_minute?: number | null;
      /** the §11.6 risk tick for the phone route */
      risk_acknowledged?: boolean;
    }) => post<{ line: Line }>('/api/lines', p),
    update: (
      id: string,
      p: {
        provider?: ProviderId;
        provider_config?: Record<string, unknown>;
        phone_number?: string;
        label?: string | null;
        active?: boolean;
        device_rate_per_minute?: number | null;
        heartbeat_alarm_minutes?: number | null;
        risk_acknowledged?: boolean;
      },
    ) => patch<{ line: Line }>(`/api/lines/${id}`, p),
    /** WHY: the server has a dedicated route; it also acknowledges the radio_error alarm (DESIGN 11.1). */
    resume: (id: string) => post<{ line: Line }>(`/api/lines/${id}/resume`),
  },

  health: {
    get: () => get<HealthResponse>('/api/health').then((r) => ({ ...r, recent_webhook_events: r.recent_webhook_events ?? r.webhook_events ?? [] })),
    /** stamps acknowledged_at/by on any alarm type and answers with the alarm as GET /health shapes it */
    ack: (id: string) => post<{ alarm: HealthEvent }>(`/api/health/events/${id}/ack`),
  },

  selfTest: {
    start: (line_id: string, to_number: string) => post<{ self_test: SelfTest }>('/api/self-test', { line_id, to_number }),
    get: (id: string) => get<{ self_test: SelfTest }>(`/api/self-test/${id}`),
    list: (line_id: string) => get<{ items: SelfTest[] }>(`/api/self-test${qs({ line_id })}`),
  },

  users: {
    list: () => get<{ items: User[] }>('/api/users').then((r) => ({ items: r.items.map(withDisabledAt) })),
    create: (p: { email: string; name: string; role: 'admin' | 'agent'; password: string }) =>
      post<{ user: User }>('/api/users', p).then((r) => ({ user: withDisabledAt(r.user) })),
    update: (id: string, p: { name?: string; role?: 'admin' | 'agent'; password?: string; disabled?: boolean }) =>
      patch<{ user: User }>(`/api/users/${id}`, p).then((r) => ({ user: withDisabledAt(r.user) })),
  },

  workspace: {
    update: (
      p: Partial<
        Pick<
          Workspace,
          | 'name'
          | 'company_name'
          | 'timezone'
          | 'send_window_start'
          | 'send_window_end'
          | 'quiet_reply_exception'
          | 'allow_quiet_hours_override'
          | 'default_assignee_user_id'
          | 'alert_webhook_url'
          | 'ai_enabled'
          | 'ai_instructions'
          | 'phone_daily_first_touch_cap'
        >
      >,
    ) => patch<{ workspace: Workspace }>('/api/workspace', p),
  },

  // ---------- phase 2 (DESIGN §12). The server routes are written from the same text; shapes here are the assumption. ----------

  ai: {
    /** 503 ai_not_configured when no key; 429 when rate-limited (show its message) */
    suggest: (conversationId: string, hint?: string | null) =>
      post<AiSuggestResponse>(`/api/conversations/${conversationId}/ai/suggest`, hint ? { hint } : {}).then((r) => ({ ...r, id: r.id ?? r.suggestion_id ?? '' })),
    /** which card was used, and (after a send) the message it became */
    choose: (suggestionId: string, p: { index: number; message_id?: string | null }) => post<Record<string, never>>(`/api/ai/suggestions/${suggestionId}/choose`, p),
    usage: () => get<AiUsageResponse>('/api/ai/usage'),
  },

  knowledge: {
    list: (kind?: KnowledgeKind) => get<{ items: KnowledgeItem[] }>(`/api/knowledge${qs({ kind })}`),
    create: (p: { kind: KnowledgeKind; title: string; body?: string | null; price_cents?: number | null; asset_id?: string | null; tags?: string[] }) =>
      post<{ item: KnowledgeItem }>('/api/knowledge', p),
    update: (id: string, p: { title?: string; body?: string | null; price_cents?: number | null; asset_id?: string | null; tags?: string[]; active?: boolean }) =>
      patch<{ item: KnowledgeItem }>(`/api/knowledge/${id}`, p),
    remove: (id: string) => del<Record<string, never>>(`/api/knowledge/${id}`),
  },

  assets: {
    /** multipart, field name `file` */
    upload: (file: File) => {
      const form = new FormData();
      form.append('file', file, file.name);
      return upload<{ asset: Asset }>('/api/assets', form);
    },
  },

  imports: {
    list: () => get<{ items: ListImport[] }>('/api/imports'),
    /** multipart: `files` (1-10, repeated) + `note` */
    create: (files: File[], note: string) => {
      const form = new FormData();
      for (const f of files) form.append('files', f, f.name);
      if (note.trim()) form.append('note', note.trim());
      return upload<{ import: ListImport }>('/api/imports', form);
    },
    /** a list the office types by hand: no files, no AI, straight to the check-and-schedule table */
    createManual: (note?: string, rows?: ImportRow[]) => post<{ import: ListImport }>('/api/imports/manual', { note: note?.trim() || null, rows: rows ?? [] }),
    get: (id: string) => get<{ import: ListImport }>(`/api/imports/${id}`),
    update: (id: string, rows: ImportRow[]) => patch<{ import: ListImport }>(`/api/imports/${id}`, { rows }),
    cancel: (id: string) => del<Record<string, never>>(`/api/imports/${id}`),
    schedule: (id: string, body: ImportScheduleBody) => post<ImportScheduleResponse>(`/api/imports/${id}/schedule`, body),
  },

  notifications: {
    me: () => get<NotificationPrefs>('/api/notifications/me'),
    update: (p: { notify_sms_number?: string | null; notify_sms?: boolean; notify_push?: boolean }) => patch<NotificationPrefs>('/api/notifications/me', p),
    /** the browser's PushSubscription.toJSON(): { endpoint, keys: { p256dh, auth } } */
    subscribe: (sub: { endpoint: string; keys: { p256dh: string; auth: string } }) => post<Record<string, never>>('/api/notifications/push-subscriptions', sub),
    unsubscribe: (endpoint: string) => del<Record<string, never>>(`/api/notifications/push-subscriptions${qs({ endpoint })}`),
    test: () =>
      post<{ sms?: { sent: boolean; detail?: string }; push?: { sent: boolean; detail?: string } }>('/api/notifications/test').then((r) => ({
        sms: r.sms?.sent === true,
        push: r.push?.sent === true,
        note: [r.sms?.detail, r.push?.detail].filter(Boolean).join(' ') || null,
      })),
  },
};

/** Plain words for any thrown error, for the little red boxes. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong.';
}
